import { extractRequestFromQueryKey } from "../../../keys";
import { removeItemFromInfiniteDataList, updateInList, upsertInInfiniteDataList, upsertInList } from "../../../utils";
import { TellescopeModelFromName } from "../../helper-types";
import { getCommonMutationSideEffectConfigs } from "../common-mutation-side-effect-configs";
import { modelMatchesGetSomeRequest } from "../filter-utils";
import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";

export const TICKETS_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"tickets"> = {
  update_indexes: (request) => {
    const affectedIds = request.updates.map(({ id }) => id);

    return {
      getOne: {
        predicate: ({ queryKey }) => {
          const queryRequest = extractRequestFromQueryKey(queryKey);
          // TODO: handle complex read filters for `getOne` requests
          return typeof queryRequest === "string" && affectedIds.includes(queryRequest);
        },
        exact: false,
        updater: (oldData) =>
          oldData
            ? {
                ...oldData,
                index: request.updates.find((update) => update.id === oldData?.id)?.index ?? oldData.index,
              }
            : oldData,
      },
      getByIds: {
        predicate: ({ queryKey }) => {
          const queryRequest = extractRequestFromQueryKey(queryKey);
          return !!queryRequest?.ids?.some((id) => affectedIds.includes(id));
        },
        exact: false,
        updater: (oldData) =>
          oldData
            ? {
                ...oldData,
                matches: oldData?.matches.map((ticket) => {
                  const update = request.updates.find((update) => update.id === ticket.id);
                  if (!update) return ticket;

                  return {
                    ...ticket,
                    index: update.index,
                  };
                }),
              }
            : oldData,
      },
      getSome: {
        exact: false,
        updater: (oldData) =>
          oldData
            ? {
                ...oldData,
                pages: oldData.pages.map((page) =>
                  page.map((ticket) => {
                    const update = request.updates.find((update) => update.id === ticket.id);
                    if (!update) return ticket;

                    return {
                      ...ticket,
                      index: update.index,
                    };
                  })
                ),
              }
            : oldData,
      },
    };
  },
  close_ticket: (request, response) => {
    const commonUpdater = getCommonMutationSideEffectConfigs<"tickets">().updateOne;

    if (!commonUpdater) return {};

    return commonUpdater(
      {
        id: request.ticketId,
        closedForReason: request.closedForReason,
        closedAt: new Date(),
      },
      response?.generated ?? response?.updated
    );
  },
  assign_from_queue: (request, originalResponse) => {
    const response =
      !!originalResponse?.queue && "cound" in originalResponse.queue
        ? {
            ...originalResponse,
            queue: {
              ...originalResponse?.queue,
              count: Math.max((originalResponse?.queue.count ?? 0) - 1),
            },
          }
        : originalResponse;

    return {
      getOne: response
        ? {
            request: response.ticket.id,
            updater: response.ticket,
          }
        : undefined,
      getSome: response
        ? {
            predicate: ({ state: { data }, queryKey }) => {
              const queryRequest = extractRequestFromQueryKey(queryKey);
              return !!(
                modelMatchesGetSomeRequest(response.ticket, queryRequest) ||
                data?.pages.some((page) => page.some((ticket) => ticket.id === response.ticket.id))
              );
            },
            updater: (oldData, { queryKey } = {}) => {
              const queryRequest = queryKey ? extractRequestFromQueryKey(queryKey) : undefined;

              const shouldFilter = queryRequest && !modelMatchesGetSomeRequest(response.ticket, queryRequest);

              if (shouldFilter) {
                return removeItemFromInfiniteDataList<TellescopeModelFromName<"tickets">, unknown>({
                  infiniteData: oldData,
                  matchFn: (ticket) => ticket.id === response.ticket.id,
                });
              }

              return upsertInInfiniteDataList<TellescopeModelFromName<"tickets">, unknown>({
                infiniteData: oldData,
                matchFn: (ticket) => ticket.id === response.ticket.id,
                updater: response.ticket,
              });
            },
          }
        : undefined,
      getByIds: response
        ? {
            predicate: ({ queryKey }) => {
              const queryRequest = extractRequestFromQueryKey(queryKey);
              return !!queryRequest?.ids?.includes(response.ticket.id);
            },
            updater: (oldData) => {
              if (!oldData) return oldData;

              return {
                matches: updateInList({
                  list: oldData.matches,
                  matchFn: (ticket) => ticket.id === response.ticket.id,
                  updater: response.ticket,
                }),
              };
            },
          }
        : undefined,
      _otherModels: {
        ticket_queues: {
          getOne: request.queueId
            ? {
                request: response?.queue.id ?? request.queueId,
                updater:
                  (response?.queue satisfies TellescopeModelFromName<"ticket_queues"> | undefined) ??
                  ((oldData) => {
                    if (!oldData) return oldData;
                    return {
                      ...oldData,
                      count: Math.max((oldData.count ?? 0) - 1, 0),
                    };
                  }),
              }
            : undefined,
          getSome: {
            predicate: ({ state: { data }, queryKey }) => {
              const queryRequest = extractRequestFromQueryKey(queryKey);
              if (response) {
                return !!(
                  modelMatchesGetSomeRequest(response.queue, queryRequest) ||
                  data?.pages.some((page) => page.some((queue) => queue.id === response.queue.id))
                );
              }

              return !!data?.pages.some((page) => page.some((queue) => queue.id === request.queueId));
            },
            updater: (oldData, { queryKey } = {}) => {
              if (!oldData) return oldData;

              const queryRequest = queryKey ? extractRequestFromQueryKey(queryKey) : undefined;
              const existingQueue: TellescopeModelFromName<"ticket_queues"> | undefined = response?.queue
                ? undefined
                : oldData.pages.reduce<TellescopeModelFromName<"ticket_queues"> | undefined>((acc, curr) => {
                    if (acc) return acc;

                    return curr.find((queue) => queue.id === request.queueId);
                  }, undefined);
              const updatedQueue =
                response?.queue ??
                (existingQueue
                  ? {
                      ...existingQueue,
                      count: Math.max((existingQueue?.count ?? 0) - 1, 0),
                    }
                  : undefined);
              if (!updatedQueue) {
                return oldData;
              }

              const shouldFilter = queryRequest && !modelMatchesGetSomeRequest(updatedQueue, queryRequest);

              if (shouldFilter) {
                return removeItemFromInfiniteDataList<TellescopeModelFromName<"ticket_queues">, unknown>({
                  infiniteData: oldData,
                  matchFn: (queue) => queue.id === updatedQueue.id,
                });
              }

              return upsertInInfiniteDataList<TellescopeModelFromName<"ticket_queues">, unknown>({
                infiniteData: oldData,
                matchFn: (queue) => queue.id === updatedQueue.id,
                updater: updatedQueue,
              });
            },
          },
          getByIds: {
            predicate: ({ queryKey }) => {
              const queryRequest = extractRequestFromQueryKey(queryKey);
              return request.queueId ? !!queryRequest?.ids?.includes(response?.queue.id ?? request.queueId) : false;
            },
            updater: (oldData) => {
              return {
                matches: upsertInList({
                  list: oldData?.matches ?? [],
                  matchFn: (queue) => queue.id === request.queueId,
                  updater: (item) =>
                    (response?.queue satisfies TellescopeModelFromName<"ticket_queues"> | undefined) ??
                    (item
                      ? {
                          ...item,
                          count: Math.max((item?.count ?? 0) - 1, 0),
                        }
                      : undefined),
                }),
              };
            },
          },
        },
      },
    };
  },
};
